import type { FilterParams } from "epsm-api-types";
import { DEFAULT_STATE } from "@/components/widgets/Screener/default";
import EarningsMomentumShowcaseImg from "@/shared/assets/stock-scanners/em_showcase.png";
import PowerdaysShowcaseImg from "@/shared/assets/stock-scanners/powerdays_showcase.png";
import TimingMomentumShowcaseImg from "@/shared/assets/stock-scanners/tm_showcase.png";
import UpdriftShowcaseImg from "@/shared/assets/stock-scanners/updrift_showcase.png";
import {
	StockScannerEvent,
	type StockScannerEventOption,
	type StockScannerFundamental,
	type StockScannerPriceAction,
	type StockScannerShowcase,
	StockScannerUniverse,
	type StockScannerUniverseOption,
} from "../../../types";

const defaultStateFilter: FilterParams | undefined =
	DEFAULT_STATE?.filter?.filter(
		(f) => !("name" in f) || f.name !== "market_cap",
	) ?? [];

export const stockScannerUniverseConfig: Record<
	StockScannerUniverse,
	StockScannerUniverseOption
> = {
	[StockScannerUniverse.LIQUID_US]: {
		label: "Liquid US",
		filter: [
			...defaultStateFilter,
			{ name: "value_traded_xd.1_D", condition: "gte", value: 2000000 },
			{ name: "value_traded_xd.2_D", condition: "gte", value: 2000000 },
			{ name: "value_traded_xd.3_D", condition: "gte", value: 2000000 },
			{ name: "value_traded_xd.5_D", condition: "gte", value: 2000000 },
			{ name: "market_cap", condition: "gte", value: 1000000 },
		],
	},
	[StockScannerUniverse.TOP_500]: {
		label: "Top 500",
		filter: [
			...defaultStateFilter,
			{
				name: "sp500_member_flag",
				condition: "in",
				value: [true],
			},
		],
	},
	[StockScannerUniverse.LARGE_MID_CAPS]: {
		label: "Large/Mid Caps",
		filter: [
			...defaultStateFilter,
			{ name: "market_cap", condition: "gte", value: 2000000000 },
		],
	},
	[StockScannerUniverse.SMALL_MICRO_CAPS]: {
		label: "Small/Micro Caps",
		filter: [
			...defaultStateFilter,
			{ name: "market_cap", condition: "gte", value: 1000000000 },
			{ name: "market_cap", condition: "lte", value: 2000000000 },
		],
	},
	[StockScannerUniverse.ETF_LIQUID]: {
		label: "ETF Liquid",
		filter: [
			{ name: "primary_listing", condition: "eq", value: true },
			{
				name: "listing_exchange_mic",
				condition: "in",
				value: ["ARCX", "BATS", "XNAS", "XNYS"],
			},
			{
				name: "code",
				condition: "in",
				value: ["ETF", "ETC"],
			},
			{ name: "value_traded_xd.1_D", condition: "gte", value: 2000000 },
			{ name: "value_traded_xd.2_D", condition: "gte", value: 2000000 },
			{ name: "value_traded_xd.3_D", condition: "gte", value: 2000000 },
			{ name: "value_traded_xd.5_D", condition: "gte", value: 2000000 },
		],
	},
	[StockScannerUniverse.ALL_STOCKS]: {
		label: "All stocks",
		filter: DEFAULT_STATE.filter ?? [],
	},
};

export const stockScannerEventConfig: Record<
	StockScannerEvent,
	StockScannerEventOption
> = {
	[StockScannerEvent.MOST_UP]: {
		label: "Most Up",
		sort: [{ name: "rt_price_chg.d1", order: -1 }],
		fields: ["ticker", "rt_price", "rt_price_chg.d1"],
		filter: [
			...defaultStateFilter,
			{ name: "rt_price_chg.d1", condition: "gte", value: 0 },
		],
		text: "Stocks with the highest upward price move in % change for the day since yesterday's close.",
	},
	[StockScannerEvent.MOST_DOWN]: {
		label: "Most Down",
		sort: [{ name: "rt_price_chg.d1", order: 1 }],
		fields: ["ticker", "rt_price", "rt_price_chg.d1"],
		filter: [
			...defaultStateFilter,
			{ name: "rt_price_chg.d1", condition: "lte", value: 0 },
		],
		text: "Stocks with the largest downward price move in % change for the day since yesterday's close.",
	},
	[StockScannerEvent.MOST_UP_CLOSE_TO_CLOSE]: {
		label: "Most Up Close To Close",
		sort: [{ name: "adj_close_pct_chg.1_D", order: -1 }],
		fields: [
			"ticker",
			"adj_close_price_xd.0_D",
			"adj_close_price_xd.1_D",
			"adj_close_pct_chg.1_D",
		],
		filter: [...defaultStateFilter],
		text: `Stocks with the largest upward price move in % change between the latest closing price and the previous day's close.
		\nThe PrChange % (Latest) is the change in percentage terms between closing periods.`,
	},
	[StockScannerEvent.MOST_DOWN_CLOSE_TO_CLOSE]: {
		label: "Most Down Close To Close",
		sort: [{ name: "adj_close_pct_chg.1_D", order: 1 }],
		fields: [
			"ticker",
			"adj_close_price_xd.0_D",
			"adj_close_price_xd.1_D",
			"adj_close_pct_chg.1_D",
		],
		filter: [...defaultStateFilter],
		text: `Stocks with the largest downward price move in % change between the latest closing price and the previous day's close.
		\nThe PrChange % (Latest) is the change in percentage terms between closing periods.`,
	},
	[StockScannerEvent.EARNINGS_MOMENTUM]: {
		label: "Earnings Momentum",
		sort: [{ name: "em_score", order: -1 }],
		fields: [
			"ticker",
			"rt_price",
			"em_score",
			"rs_abs_chg_nasdaq.ed",
			"adj_close_pct_chg.ETD",
		],
		filter: [
			...defaultStateFilter,
			{
				name: "em_score",
				condition: "gte",
				value: 10,
			},
			{
				name: "em_score",
				condition: "lte",
				value: 35.45,
			},
			{
				name: "rs_abs_chg_nasdaq.ed",
				condition: "gte",
				value: 0.1,
			},
			{
				name: "rs_abs_chg_nasdaq.ed",
				condition: "lte",
				value: 33.7,
			},
			{
				name: "adj_close_pct_chg.ETD",
				condition: "gte",
				value: 0.001,
			},
			{
				name: "adj_close_pct_chg.ETD",
				condition: "lte",
				value: 28.182,
			},
		],
		text: `Earnings Momentum (EM Score) is a powerful way to assess the strength of a company's financials. It is a multi-faceted calculation capturing the Earnings, Sales, Profit Margin and Free Cash-Flow of a stock. It also quantifies momentum and acceleration in these metrics to give you an unbiased view of the financial strength of the company. A  Positive EM Score indicates strength. Stocks with an EM of 20 or more are extraordinary.
		\nA Negative EM Score indicate companies that have no real Earnings Momentum and not worth chasing until the EM Score turns positive.`,
		image: EarningsMomentumShowcaseImg,
	},
	[StockScannerEvent.NEW_52W_HIGHS]: {
		label: "New 52W Highs",
		sort: [{ name: "adj_close_pct_chg.52_W", order: -1 }],
		fields: ["ticker", "rt_price", "rt_price_chg.d1", "adj_close_pct_chg.52_W"],
		filter: [
			...defaultStateFilter,
			{
				name: "days_since_xd_high.52_W",
				condition: "eq",
				value: 0,
			},
		],
		text: "Stocks hitting a 52 Week High today. The stocks are sorted in descending order by their 52 week % return.",
	},
	[StockScannerEvent.NEW_52W_LOWS]: {
		label: "New 52W Lows",
		sort: [{ name: "adj_close_pct_chg.52_W", order: 1 }],
		fields: ["ticker", "rt_price", "rt_price_chg.d1", "adj_close_pct_chg.52_W"],
		filter: [
			...defaultStateFilter,
			{
				name: "days_since_xd_low.52_W",
				condition: "eq",
				value: 0,
			},
		],
		text: "Stocks hitting a 52 Week Low today. The stocks are sorted on descending order by their 52 week % return.",
	},
	[StockScannerEvent.POWERDAY_POSITIVE_BREAKOUTS]: {
		label: "Powerday Positive Breakouts",
		sort: [{ name: "indicator_powerday", order: -1 }],
		fields: [
			"ticker",
			"rt_price_chg.d5",
			"indicator_powerday",
			"powerday_trend_status",
			"trend_indicator_final_status",
		],
		filter: [
			...defaultStateFilter,
			{
				name: "indicator_powerday",
				condition: "gte",
				value: 0.1,
			},
			{
				name: "indicator_powerday",
				condition: "lte",
				value: 15.17,
			},
		],
		text: `The Powerday number is a calculation of how many Standard Deviations Prices and Value traded are registered for that specific Powerday.
		\nThese stocks have A Positive Trend powerdays and are in break "Up" configuration possibly ready to rally.
		\nPowerday Score: This represents the "strength" of the win from the last "Bull and Bear battle".
		\nPowerday Trend: The trend defined by the Powerday. It is either positive or negative, pending or confirmed.
		\nTrend Signal: This is defined as the stock's ATR (Av. True Range) and the distance between two short term moving averages.`,
	},
	[StockScannerEvent.POWERDAY_NEGATIVE_BREAKOUTS]: {
		label: "Powerday Negative Breakouts",
		sort: [{ name: "indicator_powerday", order: 1 }],
		fields: [
			"ticker",
			"rt_price_chg.d5",
			"indicator_powerday",
			"powerday_trend_status",
			"trend_indicator_final_status",
		],
		filter: [
			...defaultStateFilter,
			{
				name: "indicator_powerday",
				condition: "gte",
				value: -9.26,
			},
			{
				name: "indicator_powerday",
				condition: "lte",
				value: -0.1,
			},
			{
				name: "rt_price_chg.d5",
				condition: "lte",
				value: -0.01,
			},
		],
		text: `The Powerday number is a calculation of how many Standard Deviations Prices and Value traded are registered for that specific Powerday.
		\nThese stocks have Negative Trend powerdays and are in break "down" configuration possibly heading downward.
		\nPowerday Score: This represents the "strength" of the win from the last "Bull and Bear battle".
		\nPowerday Trend: The trend defined by the Powerday. It is either positive or negative, pending or confirmed.
		\nTrend Signal: This is defined as the stock's ATR (Av. True Range) and the distance between two short term moving averages.`,
	},
	[StockScannerEvent.UP_SLOPING_TRENDING]: {
		label: "Up Sloping/Trending",
		sort: [{ name: "trend_indicator_statuses_per_days.UP_TREND", order: -1 }],
		fields: [
			"ticker",
			"trend_indicator_final_status",
			"trend_indicator_statuses_per_days.UP_TREND",
			"rt_price_chg.d20",
		],
		filter: [
			...defaultStateFilter,
			{ name: "price_slope_today.10_D", condition: "gte", value: 0.01 },
			{ name: "price_slope_10d_ago.10_D", condition: "gte", value: 0.01 },
			{ name: "price_slope_20d_ago.10_D", condition: "gte", value: 0.01 },
			{ name: "price_slope_60d_ago.40_D", condition: "gte", value: 0.01 },
			{
				name: "trend_indicator_final_status",
				condition: "in",
				value: ["Up Trend"],
			},
			{ name: "price_momentum_signal", condition: "in", value: ["Up Moment"] },
			{
				name: "slope_price_momentum_signal",
				condition: "in",
				value: ["Heading Upward"],
			},
		],
		text: `These stocks are showing a constant upward sloping trend over the past 60 days.
		\nThe Slope of the stock price is an indication of the direction of the trend and the stock's Price Momentum.`,
	},
	[StockScannerEvent.DOWN_SLOPING_TRENDING]: {
		label: "Down Sloping/Trending",
		sort: [{ name: "trend_indicator_statuses_per_days.DOWN_TREND", order: -1 }],
		fields: [
			"ticker",
			"trend_indicator_final_status",
			"trend_indicator_statuses_per_days.DOWN_TREND",
			"rt_price_chg.d20",
		],
		filter: [
			...defaultStateFilter,
			{ name: "price_slope_today.10_D", condition: "lte", value: 0 },
			{ name: "price_slope_10d_ago.10_D", condition: "lte", value: 0 },
			{ name: "price_slope_20d_ago.10_D", condition: "lte", value: 0 },
			{ name: "price_slope_60d_ago.40_D", condition: "lte", value: 0 },
			{
				name: "trend_indicator_final_status",
				condition: "in",
				value: ["Down Trend"],
			},
			{
				name: "price_momentum_signal",
				condition: "in",
				value: ["Down Moment"],
			},
			{
				name: "slope_price_momentum_signal",
				condition: "in",
				value: ["Heading Downward"],
			},
		],
		text: `These stocks are showing a constant downward sloping trend for the past 60 days.
		\nThe slope of the stock price is an indication of the direction of the trend and the stock's Price Momentum.`,
	},
	[StockScannerEvent.WALL_STREET_FAVS]: {
		label: "Wall Street Favs",
		sort: [{ name: "rs_abs_chg_nasdaq.ed", order: -1 }],
		fields: [
			"ticker",
			"rt_price_chg.d5",
			"analyst_recommendation.0_D",
			"rs_abs_chg_nasdaq.ed",
		],
		filter: [
			...defaultStateFilter,
			{ name: "analyst_recommendation.0_D", condition: "gte", value: 1.25 },
			{ name: "rs_abs_chg_nasdaq.ed", condition: "gte", value: 0.1 },
		],
		text: `Wall Street analysts provide their opinion and research on a regular basis. They rank stocks usually on the following scale:
		\n1-Strong Buy, 2-Buy, 3-Hold, 4-Sell, 5-Strong Sell.
		\nThe stock's that make the list have also seen an increase in their Earnings Revision Score since their last earnings release.`,
	},
	[StockScannerEvent.PRICE_TARGET_RAISED]: {
		label: "Price Target Raised",
		sort: [{ name: "tp_cnt_est_rev_up", order: -1 }],
		fields: ["ticker", "rt_price", "tp_cnt_est_rev_up", "tp_est.mean"],
		filter: [
			...defaultStateFilter,
			{ name: "tp_cnt_est_rev_up", condition: "gte", value: 0 },
		],
		text: `This list are stocks that have had their price targets raised several times over the last few days. This indicates a consensus positive view on the stock by Wall Street analysts.
		\nThe Target Price Mean is the average price of the price targets and can be used as a guide for future price movement.`,
	},
	[StockScannerEvent.REVISION_WINNERS]: {
		label: "Revision Winners",
		sort: [{ name: "rs_abs_chg_nasdaq.ed", order: -1 }],
		fields: [
			"ticker",
			"rt_price",
			"rs_abs_chg_nasdaq.ed",
			"rss_abs_chg_nasdaq.ed",
		],
		filter: [
			...defaultStateFilter,
			{ name: "rs_abs_chg_nasdaq.ed", condition: "gte", value: 0.1 },
			{ name: "rss_abs_chg_nasdaq.ed", condition: "gte", value: 0.1 },
		],
		text: `Stocks that have experienced a significant Estimates Revision in both their Sales and Earnings (EPS) estimates.
		\nAnalysts usually take a few days to update their estimates and during the quarter, revise them Up or Down depending of their view of the stock and market environment.
		\nPositive Estimates Revisions are very often a catalyst for stock outperformance.`,
	},
	[StockScannerEvent.RELATIVE_STRENGTH]: {
		label: "Relative Strength",
		sort: [{ name: "composite_10d_slope", order: -1 }],
		fields: [
			"ticker",
			"rt_price_chg.d20",
			"composite_10d_slope",
			"composite_score",
		],
		filter: [
			...defaultStateFilter,
			{ name: "composite_score", condition: "gte", value: 75 },
			{ name: "composite_10d_slope", condition: "gte", value: 10 },
		],
		text: `The Relative Market Rank (RMR) is a representation of the major strength of a stock compared to all of the stocks listed in the US with a market cap above $50m.
		\nThe 10 day slope of the RMR allows investors to capture those stocks outperforming the most.
		\nStocks are in the top quartile in terms of performance (RMR>75) and have experienced a very fast increase in their rank recently.`,
	},
	[StockScannerEvent.GARP]: {
		label: "GARP",
		sort: [{ name: "earnings_yield", order: -1 }],
		fields: ["ticker", "peg_ttm", "revenuegrowth.0_Y", "earnings_yield"],
		filter: [
			...defaultStateFilter,
			{ name: "revenuegrowth.1_Y", condition: "gte", value: 0.04 },
			{ name: "revenuegrowth.0_Y", condition: "gte", value: 0.04 },
			{ name: "earnings_yield", condition: "gte", value: 0.04 },
			{ name: "netincomegrowth.0_Y", condition: "gte", value: 0.06 },
			{ name: "peg_ttm", condition: "gte", value: 0 },
			{ name: "peg_ttm", condition: "lte", value: 1.75 },
			{ name: "peg_ftm", condition: "gte", value: 0 },
			{ name: "peg_ftm", condition: "lte", value: 1.75 },
		],
		text: `Growth at a Reasonable Price stocks exhibit decent growth with PEG ratios both Trailing 12 months and 12 months Forward that are still at reasonable valuations.
		\nThe list identifies companies growing revenues with a reasonable valuation and an attractive earnings yield. This helps investors gauge the company's profitability in relation to its current stock price.`,
	},
	[StockScannerEvent.CAPITAL_EFFICIENCY]: {
		label: "Capital Efficiency",
		sort: [{ name: "roe.0_Y", order: -1 }],
		fields: ["ticker", "roic.0_Y", "roa.0_Y", "roe.0_Y"],
		filter: [
			...defaultStateFilter,
			{ name: "roe.0_Y", condition: "gte", value: 0.35 },
			{ name: "roa.0_Y", condition: "gte", value: 0.15 },
			{ name: "roic.0_Y", condition: "gte", value: 0.2 },
		],
		text: `Stocks showing most efficient use of Capital is exhibited here with high ROIC ROE and ROA ratios .These stocks are successful at managing and producing a strong return with their capital.
		\nAn investor would want to consider enhancing this scan with Momentum filtering.`,
	},
	[StockScannerEvent.HIGH_GROWTH]: {
		label: "High Growth",
		sort: [{ name: "eps_growth.street_q_to_4q_back", order: -1 }],
		fields: [
			"ticker",
			"eps_growth.street_q_to_4q_back",
			"eps_growth.street_q_back_to_5q_back",
			"eps_estimates_growth.bnri_q_to_q4_forward",
		],
		filter: [
			...defaultStateFilter,
			{
				name: "eps_growth.street_q_to_4q_back",
				condition: "gte",
				value: 0.2,
			},
			{
				name: "eps_growth.street_q_back_to_5q_back",
				condition: "gte",
				value: 0.1,
			},
			{
				name: "eps_estimates_growth.bnri_q_to_q4_forward",
				condition: "gte",
				value: 0.1,
			},
			{
				name: "combined_acceleration_curr_and_1_q_back.street",
				condition: "gte",
				value: 0.001,
			},
		],
		text: `Stocks experiencing very high EPS growth. Greater than 10% EPS growth the previous quarter and more than 20% the the last reported quarter.
		\nTo make the list, these stocks are also growing their Sales by 20% or more and have Forward EPS estimates for the next quarter and next 12 months of 10% or more.
		\nIdentifying true growth stocks is important because when growth momentum is in place, it can remain for several subsequent quarters.`,
	},
	[StockScannerEvent.INCOME_BEST]: {
		label: "Income Best",
		sort: [{ name: "dividend_yield", order: -1 }],
		fields: ["ticker", "dividend_yield", "market_cap", "pe_ttm_ratio"],
		filter: [
			...defaultStateFilter,
			{ name: "dividend_yield", condition: "gte", value: 0.04 },
			{
				name: "sales_growth.sales_q_to_4q_back",
				condition: "gte",
				value: 0.01,
			},
			{
				name: "eps_growth.street_q_to_4q_back",
				condition: "gte",
				value: 0.01,
			},
			{ name: "pe_ttm_ratio", condition: "gte", value: 0.1 },
		],
		text: `Dividend paying companies can offer a very attractive Dividend Yield depending on their present market price. Dividend stocks usually have lower price volatility.
		\nThis list of companies are growing their EPS and Sales over the last quarter and offer a dividend yield in excess of 4%.`,
	},
	[StockScannerEvent.VALUE_PLAYS]: {
		label: "Value Plays",
		sort: [{ name: "market_cap", order: -1 }],
		fields: [
			"ticker",
			"adj_close_pct_chg.YTD",
			"pe_ttm_ratio",
			"price_to_revenue",
		],
		filter: [
			...defaultStateFilter,
			{ name: "pe_ttm_ratio", condition: "gte", value: 2 },
			{ name: "pe_ttm_ratio", condition: "lte", value: 10 },
			{ name: "price_to_revenue", condition: "lte", value: 2 },
			{ name: "price_to_book", condition: "lte", value: 1.5 },
			{ name: "eps_growth.street_q_to_4q_back", condition: "gte", value: 1.5 },
		],
		text: `This list of stocks are value stocks growing at a reasonable price and have very low Price to Book, Price to Sales and Price to Earnings ratios.
		\nValue stocks are relatively "Lower Risk" than their more expensive peers and have reasonable earnings yields. Their value lies in their product and market position.`,
	},
	[StockScannerEvent.ACCELERATING]: {
		label: "Accelerating",
		sort: [
			{ name: "combined_acceleration_curr_and_1_q_back.street", order: -1 },
		],
		fields: [
			"ticker",
			"combined_acceleration_curr_and_1_q_back.street",
			"sales_acceleration_curr_and_1_q_back",
			"combined_sequential_positive_acc.street",
		],
		filter: [
			...defaultStateFilter,
			{
				name: "eps_growth.street_q_to_4q_back",
				condition: "gte",
				value: 0.01,
			},
			{
				name: "sales_growth.sales_q_to_4q_back",
				condition: "gte",
				value: 0.01,
			},
			{ name: "sales_sequential_positive_acc", condition: "gte", value: 1 },
			{
				name: "combined_sequential_positive_acc.street",
				condition: "gte",
				value: 2,
			},
			{
				name: "combined_acceleration_curr_and_1_q_back.street",
				condition: "gte",
				value: 0.01,
			},
			{
				name: "sales_acceleration_curr_and_1_q_back",
				condition: "gte",
				value: 0.01,
			},
		],
		text: `These stocks have reported increasing EPS and Sales for a minimum of two consecutive quarters. To make the list, the stocks additionally have to show sequential acceleration in both from the prior quarter.
		\nThe % Change in EPS and Sales acceleration rates are displayed along with the number of sequential quarters of EPS acceleration.`,
	},
	[StockScannerEvent.DECELERATING]: {
		label: "Decelerating",
		sort: [
			{ name: "combined_acceleration_curr_and_1_q_back.street", order: 1 },
		],
		fields: [
			"ticker",
			"combined_acceleration_curr_and_1_q_back.street",
			"sales_acceleration_curr_and_1_q_back",
			"combined_sequential_negative_acc.street",
		],
		filter: [
			...defaultStateFilter,
			{
				name: "eps_growth.street_q_to_4q_back",
				condition: "lte",
				value: -0.01,
			},
			{
				name: "sales_growth.sales_q_to_4q_back",
				condition: "lte",
				value: -0.01,
			},
			{ name: "sales_sequential_negative_acc", condition: "gte", value: 1 },

			{
				name: "combined_sequential_negative_acc.street",
				condition: "gte",
				value: 2,
			},
			{
				name: "combined_acceleration_curr_and_1_q_back.street",
				condition: "lte",
				value: -0.01,
			},
			{
				name: "sales_acceleration_curr_and_1_q_back",
				condition: "lte",
				value: -0.01,
			},
		],
		text: "These stocks are experiencing two sequential (last & prior) quarters of falling EPS and Sales YOY. To make the list, there is an increase in the deceleration rate from the previous quarter.",
	},
	[StockScannerEvent.TIMING_MOMENTUM]: {
		label: "Timing Momentum",
		fields: [
			"ticker",
			"rt_price_chg.d5",
			"powerday_trend_status",
			"slope_price_momentum_signal",
			"trend_indicator_final_status",
		],
		filter: [
			...defaultStateFilter,
			{
				name: "trend_indicator_final_status",
				condition: "in",
				value: ["Up Trend"],
			},
			{
				name: "distance_moving_average.ema20.ema50",
				condition: "lte",
				value: 0,
			},
			{ name: "composite_10d_slope", condition: "gte", value: 0 },
			{ name: "powerday_trend_status", condition: "in", value: ["Positive"] },
			{
				name: "slope_price_momentum_signal",
				condition: "in",
				value: ["Heading Upward"],
			},
		],
		text: `These stocks are in full uptrend. Their price has broken out from a recent consolidation range. The Momentum slope is pointing upward, suggesting further upward price movement.
		\nTheir Relative Market Rank (RMR), which compares them to all the other stock's price performance over time has been rising, suggesting investor demand.
		\nTo make this list, their last Powerday was positive suggesting that the "Bulls beat the Bears" in the last battle. This frequently leads to a continued up-trend for the stock.
		\nThe stocks are finally checked by ensuring the current stock price is above its 20 and 50 Exponential Moving Averages (EMA).`,
		image: TimingMomentumShowcaseImg,
	},
	[StockScannerEvent.UPDRIFT]: {
		label: "Updrift",
		sort: [{ name: "accuracy.drift_end", order: -1 }],
		fields: [
			"ticker",
			"distance_to_max.max_qtr_end",
			"distance_to_max.sigma1_qtr_end",
			"distance_to_avg.avg_qtr_end",
			"accuracy.drift_end",
		],
		filter: [
			...defaultStateFilter,
			{
				name: "accuracy.drift_end",
				condition: "gte",
				value: 0.66,
			},
			{
				name: "sample",
				condition: "gte",
				value: 6,
			},
			{
				name: "drift_name",
				condition: "in",
				value: ["Positive Underreaction (PU)"],
			},
		],
		text: `The Post Earnings Announcement Drift (PEAD) is a well established and widely followed market phenomenon.
		\nStocks will immediately respond to new earnings data and how it compares to guidance and market expectations. This initial response frequently leads to over/under reactions in price movement. With time, rationality resumes with the stock price truly reflecting the magnitude of the most recent earnings "Beat or Miss" vs expectations.
		\nThe price forecast cone is displayed in this list of stocks and shows potential % return if the stock reaches it's upper forecast, at 1 Standard Deviation and at the average forecast. Stocks frequently revert to the mean so this acts as an important guide as to where price is most likely to settle from a historical and statistical viewpoint.
		\nThe Drift forecast accuracy rate is displayed and is an important historical guide for future performance.`,
		image: UpdriftShowcaseImg,
	},
	[StockScannerEvent.POWERDAYS]: {
		label: "Powerdays",
		sort: [{ name: "indicator_powerday", order: -1 }],
		fields: [
			"ticker",
			"trend_indicator_final_status",
			"powerday_trend_status",
			"indicator_powerday",
			"rt_price_chg.d5",
		],
		filter: [
			...defaultStateFilter,
			{
				name: "powerday_trend_status",
				condition: "in",
				value: ["Positive", "Postv. Pendg"],
			},
			{
				name: "trend_indicator_final_status",
				condition: "in",
				value: ["Up Trend"],
			},
			{
				name: "avg_daily_value_traded_xd.20_D",
				condition: "gte",
				value: 2000000,
			},
		],
		text: `These stocks are in a confirmed Trend from both their price action and their "Powerday" trends. They have a combination of momentum and major players pushing the stock up.
		\nPowerdays is an analytic capturing the infrequent battles between Bulls and Bears. During this market "fight" which could last several days, the opposing forces will battle and only one side will be victorious.
		\nIn that battle, "data clues" in price moves and volume sizes show the true scale of the "market players" (major institutions or retail investors).
		\nThe winning side will "define" and set a trend for next few days or weeks to come. During this market "fight" which could last several days, Bulls and Bears will battle and only one side will be victorious.
		\nThe winning side will "define" or set a trend for a few days or weeks.
		\nTrend: Can be up or down or consolidating. The price trend is defined by the distance to moving average in ATR (Average True Range) terms.
		\nPowerday Trend: The trend is either positive or negative, pending or confirmed.
		\nThe Powerday number is a calculation of how many Standard Deviations Price and Value traded are for that specific day.`,
		image: PowerdaysShowcaseImg,
	},
	[StockScannerEvent.UP_VOLUME]: {
		label: "Up Volume",
		sort: [{ name: "rt_volume_ratio", order: -1 }],
		fields: [
			"ticker",
			"rt_price",
			"rt_price_chg.d1",
			"rt_volume_ratio",
			"trend_indicator_final_status",
		],
		filter: [
			...defaultStateFilter,
			{
				name: "rt_price_chg.d1",
				condition: "gte",
				value: 0.01,
			},
		],
		text: `These stocks have very high and abnormal volume for the day. They are also having a price move greater than 1%.
		\nUsually abnormal volume signals a "Strong" push in the direction of the trend.`,
	},
	[StockScannerEvent.DOWN_VOLUME]: {
		label: "Down Volume",
		sort: [{ name: "rt_volume_ratio", order: -1 }],
		fields: [
			"ticker",
			"rt_price",
			"rt_price_chg.d1",
			"rt_volume_ratio",
			"trend_indicator_final_status",
		],
		filter: [
			...defaultStateFilter,
			{
				name: "rt_price_chg.d1",
				condition: "lte",
				value: -0.01,
			},
		],
		text: `These stocks are down and have very high and abnormal volume for the day. They are also having a price move downwards in excess of 1%.
		\nUsually abnormal volume signals a "Strong" push in the direction of the trend.`,
	},
	[StockScannerEvent.RELATIVE_WEAKNESS]: {
		label: "Relative Weakness",
		sort: [{ name: "composite_10d_slope", order: 1 }],
		fields: [
			"ticker",
			"rt_price_chg.d5",
			"composite_score",
			"composite_10d_slope",
			"one_year_score",
		],
		filter: [
			...defaultStateFilter,
			{
				name: "one_year_score",
				condition: "gte",
				value: 50,
			},
			{
				name: "composite_score",
				condition: "lte",
				value: 50,
			},
		],
		text: `The Relative Market Rank (RMR) is a representation of the major strength of a stock compared to all of the stocks listed in the US.
		\nThese stocks have a 1 Year RMR greater than 50 but have seen a precipitous drop in their composite RMR signalling a major weakness.
		\nThey are becoming out of favor among investors who are shifting money to other stocks.
		\nThese stocks are sorted from the best to the worst in terms of their 10 day RMR composite slope.`,
	},
};

export const stockScannerFundamentals: StockScannerFundamental[] = [
	StockScannerEvent.HIGH_GROWTH,
	StockScannerEvent.ACCELERATING,
	StockScannerEvent.DECELERATING,
	StockScannerEvent.GARP,
	StockScannerEvent.VALUE_PLAYS,
	StockScannerEvent.INCOME_BEST,
	StockScannerEvent.WALL_STREET_FAVS,
	StockScannerEvent.PRICE_TARGET_RAISED,
	StockScannerEvent.REVISION_WINNERS,
	StockScannerEvent.RELATIVE_STRENGTH,
	StockScannerEvent.RELATIVE_WEAKNESS,
	StockScannerEvent.CAPITAL_EFFICIENCY,
];

export const stockScannerPriceActions: StockScannerPriceAction[] = [
	StockScannerEvent.MOST_UP,
	StockScannerEvent.MOST_DOWN,
	StockScannerEvent.UP_VOLUME,
	StockScannerEvent.DOWN_VOLUME,
	StockScannerEvent.MOST_UP_CLOSE_TO_CLOSE,
	StockScannerEvent.MOST_DOWN_CLOSE_TO_CLOSE,
	StockScannerEvent.NEW_52W_HIGHS,
	StockScannerEvent.NEW_52W_LOWS,
	StockScannerEvent.POWERDAY_POSITIVE_BREAKOUTS,
	StockScannerEvent.POWERDAY_NEGATIVE_BREAKOUTS,
	StockScannerEvent.UP_SLOPING_TRENDING,
	StockScannerEvent.DOWN_SLOPING_TRENDING,
];

export const StockScannerShowcases: StockScannerShowcase[] = [
	StockScannerEvent.EARNINGS_MOMENTUM,
	StockScannerEvent.TIMING_MOMENTUM,
	StockScannerEvent.UPDRIFT,
	StockScannerEvent.POWERDAYS,
];
